import { NgClass, NgFor, NgIf, NgStyle } from "@angular/common";
import { Component, HostListener, OnInit, viewChild } from "@angular/core";
import { ExtendedModule } from "@angular/flex-layout/extended";
import { FlexModule } from "@angular/flex-layout/flex";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatAccordion, MatExpansionPanelDescription } from "@angular/material/expansion";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatToolbar } from "@angular/material/toolbar";
import { MatTooltip } from "@angular/material/tooltip";
import { RouterLink, RouterLinkActive } from "@angular/router";

import { UserBio } from "src/app/models/UserBio";
import { LocalStorageService } from "src/app/service/localStorage.service";
import { CookieService } from "src/app/shared/services/cookie.service";
import { EmployeeService } from "src/app/shared/services/employee.service";
import { NotificationService } from "src/app/shared/services/notification.service";
import { PersonaService } from "src/app/shared/services/persona.service";
import { environment } from "src/environments/environment";
import { version } from "../../../version";
import { CommonGraphicComponent } from "../../common/common-graphic/common-graphic.component";
import { CommonLazyLoaderComponent } from "../../common/common-lazyloader/common-lazyloader.component";
import { CommonSearchComponent } from "../../common/common-search/common-search.component";
import { Theme } from "../../models/theme.model";
import { SmallBioComponent } from "../../routes/my-profile/bio/small-bio/small-bio.component";
import { InitialsPipe } from "../../routes/pipes/Initials.pipe";
import { BackendService } from "../../service/backend.service";
import { DeviceService } from "../../service/device.service";
import { NavigationService } from "../../service/navigation.service";
import { ThemeService } from "../../service/theme.service";
import { NotificationMenuComponent } from "../notification-menu/notification-menu.component";
import { AuthService } from "@mes-slalom/ui-common-lib";

const PERSONAS = [
  { employeeId: "P001", title: "Consultant Leader" },
  { employeeId: "P002", title: "Consultant" },
  { employeeId: "P004", title: "Consultant Director" },
  { employeeId: "P003", title: "_build Engineer" },
  { employeeId: "P007", title: "_build Director" },
  { employeeId: "P006", title: "Internal Accountant" },
];

@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  imports: [
    MatToolbar,
    NgClass,
    ExtendedModule,
    NgIf,
    MatIconButton,
    MatIcon,
    RouterLink,
    FlexModule,
    CommonSearchComponent,
    MatButton,
    NotificationMenuComponent,
    CommonLazyLoaderComponent,
    MatMenuTrigger,
    NgStyle,
    MatMenu,
    MatMenuItem,
    SmallBioComponent,
    NgFor,
    CommonGraphicComponent,
    RouterLinkActive,
    MatAccordion,
    MatExpansionPanelDescription,
    MatTooltip,
    InitialsPipe,
  ],
})
export class NavbarComponent implements OnInit {
  version = "";
  searchActive = false;
  darkModeEnabled = false;
  theme = "slalom-light-theme";
  colorThemes = [
    { themeId: "slalom-light-theme", label: "Light" },
    { themeId: "slalom-dark-theme", label: "Dark" },
    { themeId: "midnight-dark-theme", label: "Midnight" },
  ];
  user: UserBio;
  isMobile: boolean = false;
  hasAltTheme;
  showNotifications = environment.showNotifications;
  showPersonas = environment.showPersona;
  selectablePersonas: any[] = [];
  notifications = [];
  readonly accordion = viewChild(MatAccordion);

  constructor(
    public navigationService: NavigationService,
    public notificationService: NotificationService,
    public deviceService: DeviceService,
    public backendService: BackendService,
    public employeeService: EmployeeService,
    private themeService: ThemeService,
    private cookieService: CookieService,
    private personaService: PersonaService,
    private localstorageService: LocalStorageService,
    private authService: AuthService
  ) {
    this.version = version;
  }

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.checkScreenSize();
  }

  // calculate screen size when screen resizes
  checkScreenSize() {
    this.isMobile = window.innerWidth < 960;
  }

  ngOnInit(): void {
    this.employeeService.getBio().subscribe((user) => {
      this.user = user;
      if (!this.themeService.activeTheme) {
        this.user.isBuild
          ? this.themeService.changeTheme("midnight-dark-theme")
          : this.themeService.changeTheme("slalom-light-theme");
      }
      this.theme = this.themeService.activeTheme;
    });
    if (environment.showNotifications) {
      this.notificationService.getNotifications().subscribe((notifications) => {
        this.notifications = notifications;
      });
    }
    if (this.showPersonas) {
      PERSONAS.forEach((persona) => {
        this.employeeService.getBio(persona.employeeId).subscribe((bio) => {
          const mappedPersona = bio as any;
          mappedPersona.personaTitle = persona.title;
          this.selectablePersonas.push(mappedPersona);
          this.selectablePersonas.sort((a, b) => a.employeeId.localeCompare(b.employeeId));
        });
      });
    }
    if (environment.showNotifications) {
      this.notificationService.getNotifications().subscribe((notifications) => {
        notifications.forEach((notification) => {
          this.employeeService.getBio(notification.relatedEmployee.employeeId).subscribe((bio) => {
            notification.photo = bio.photo;
            notification.name = bio.name;
          });
        });
      });
    }
  }

  setPersona(employeeId) {
    if (employeeId) {
      this.personaService.setImpersonation(employeeId).subscribe((r) => {
        this.localstorageService.deleteAll();
        this.cookieService.deleteAll();
        this.authService.logout();
      });
    } else {
      this.personaService.clearImpersonation().subscribe((r) => {
        this.localstorageService.deleteAll();
        this.cookieService.deleteAll();
        this.authService.logout();
      });
    }
  }

  closeSearch(): void {
    this.searchActive = false;
  }

  updateTheme(event, themeId) {
    event.stopPropagation();
    this.theme = themeId;
    this.themeService.changeTheme(this.theme as Theme);
  }

  toggleSearch() {
    this.searchActive = !this.searchActive;
  }
}
